<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { AccountManagerCard } from '@/api/queries/accountManagers';

type ActionUnit = { name: string; title: string; price_min: number; currency?: string };

// The project's actions in one place (same four as the app's Overview tiles). The shell decides
// which ones this viewer gets; nothing here knows about access levels.
const {
  showOffer = false,
  postTo = null,
  showRegister = false,
  showContact = false,
  accountManager = null,
  brochureUrl = null,
  units = [],
  showUnitSelect = false,
} = defineProps<{
  showOffer?: boolean;
  postTo?: RouteLocationRaw | null;
  showRegister?: boolean;
  showContact?: boolean;
  accountManager?: AccountManagerCard | null;
  brochureUrl?: string | null;
  units?: ActionUnit[];
  showUnitSelect?: boolean;
}>();

const selectedUnit = defineModel<string | null>('selectedUnit', { default: null });

const emit = defineEmits<{
  (e: 'generate-offer'): void;
  (e: 'register'): void;
}>();

const { t } = useI18n();
const { formatAmount } = useAedMoneyDisplay();

// Same number as the support bubble: an agent with no manager assigned still reaches the desk.
const DEFAULT_DESK_WHATSAPP = '971527033506';

const managerName = computed(() => {
  const name = `${accountManager?.firstName ?? ''} ${accountManager?.lastName ?? ''}`.trim();
  return name || t('property.accountManagerSidebar.fallbackName');
});

const whatsappLink = computed(() => {
  const digits = accountManager?.phone?.replace(/\D/g, '') || DEFAULT_DESK_WHATSAPP;
  return `https://wa.me/${digits}`;
});

const hasContent = computed(() => showOffer || !!postTo || showRegister || showContact || !!brochureUrl);

const TILE_CLASS =
  'flex min-h-[76px] flex-col items-start justify-between gap-2 rounded-xl border p-3 text-start text-[13px] font-bold leading-tight text-black-100 transition-colors';
const SECONDARY_TILE_CLASS = `${TILE_CLASS} border-black/[0.12] bg-white hover:bg-black/[0.03]`;
</script>

<template>
  <section
    v-if="hasContent"
    class="flex flex-col gap-3 rounded-2xl border border-black/[0.08] bg-white p-4"
    data-testid="project-actions"
  >
    <div v-if="showUnitSelect && units.length > 1">
      <label
        for="pp-action-unit-select"
        class="mb-1.5 block text-[12px] font-semibold text-black-60"
        >{{ t('property.commissionBlock.unitTypeLabel') }}</label
      >
      <select
        id="pp-action-unit-select"
        v-model="selectedUnit"
        class="h-10 w-full rounded-[10px] border border-black/15 bg-white px-3 text-[13px] font-medium text-black-100"
        data-testid="commission-unit-selector"
      >
        <option
          v-for="unit in units"
          :key="unit.name"
          :value="unit.name"
        >
          {{ unit.title }} — {{ t('common.priceTable.from') }}
          {{ formatAmount(unit.price_min, unit.currency || 'AED') }}
        </option>
      </select>
    </div>

    <div class="grid grid-cols-2 gap-2 [&>*:last-child:nth-child(odd)]:col-span-2">
      <button
        v-if="showOffer"
        type="button"
        :class="[TILE_CLASS, 'border-gold-primary-100 bg-gold-primary-100 hover:opacity-90']"
        data-testid="project-action-offer"
        @click="emit('generate-offer')"
      >
        <UIIcon
          name="file-text"
          size="sm"
        />
        {{ t('property.heroCard.generateOfferCta') }}
      </button>
      <NuxtLink
        v-if="postTo"
        :to="postTo"
        :class="SECONDARY_TILE_CLASS"
        data-testid="project-action-post"
      >
        <UIIcon
          name="edit"
          size="sm"
        />
        {{ t('property.actionBlock.marketingPost') }}
      </NuxtLink>
      <button
        v-if="showRegister"
        type="button"
        :class="SECONDARY_TILE_CLASS"
        data-testid="register-this-unit-cta"
        @click="emit('register')"
      >
        <UIIcon
          name="deal"
          size="sm"
        />
        {{ t('property.actionBlock.registerDeal') }}
      </button>
      <a
        v-if="showContact"
        :href="whatsappLink"
        target="_blank"
        rel="noopener noreferrer"
        :class="SECONDARY_TILE_CLASS"
        data-testid="project-action-contact"
      >
        <UIIcon
          name="whatsapp"
          size="sm"
        />
        <span>
          {{ t('property.actionBlock.contactAm') }}
          <span class="block text-[11.5px] font-medium text-black-60">{{ managerName }}</span>
        </span>
      </a>
    </div>

    <a
      v-if="brochureUrl"
      :href="brochureUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="text-center text-[13px] font-semibold text-black-100 underline underline-offset-2"
      data-testid="project-action-brochure"
    >
      {{ t('property.heroCard.downloadBrochure') }}
    </a>
  </section>
</template>
